import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { DataStudioRedesignService } from 'app/shared/services/data-services/data-studio-redesign/data-studio-redesign.service';
import { WarningsService } from 'app/data-studio-redesign/services/warnings.service';
import { DataStudioHelperService } from 'app/data-studio-redesign/services/data-studio-helper.service';
import { DataStudioGranularityType, DataStudioQueryType, Stream } from 'app/shared/services/data-services/graphql';
import { filter, map, takeUntil } from 'rxjs/operators';
import { EThemeName, ThemeService } from '@wind-log_library/fe-component-library';
import { DateTimeFormatService } from 'app/shared/services/date-time-format/date-time-format.service';
import {
  AGG_TYPES,
  AggregationType,
  CSDataWithSignals,
  CSSignal,
  DataSelectOptions,
  DataStudioParks,
  DevicePark,
  DevicesList,
  DeviceType,
  DSSignalItemData,
  Metmast,
  MetmastDevicePark,
  MetmastsDataWithSignals,
  MetmastSignal,
  MinMaxSignals,
  QueryParams,
  SelectedParksTurbines,
  SignalList,
  SignalMultiDevicesList,
  TurbineDataStudioLive,
  TurbineDataWithSignals,
  TurbineSignal
}
  from 'app/shared/models/data-studio.model';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import {
  createCSDeviceSignalsMapper,
  createCSSignalDevicesMapper,
  createMetmastsDeviceSignalsMapper,
  createMetmastsSignalDevicesMapper,
  createTurbineDeviceSignalsMapper,
  createTurbineSignalDevicesMapper,
  createTurbineSignalTypesMaper
} from 'app/data-studio-redesign/helper/data-studio-signal.helper';
import { TranslateService } from '@ngx-translate/core';
import { DEVICE_TYPES } from 'app/data-studio-redesign/interfaces/data-studio.interface';

@Component({
  selector: 'app-data-live-data',
  templateUrl: './data-live-data.component.html',
  styleUrls: ['./data-live-data.component.scss'],
  standalone: false
})
export class DataLiveDataComponent implements OnInit, OnDestroy {
  showPreview: boolean = false;
  bsConfig: Observable<Partial<BsDatepickerConfig>>;
  bsRangeValue: Date[];
  minDate: Date = new Date('01-01-2019');
  maxDate: Date = new Date();
  selectedAggregation: AggregationType = AGG_TYPES.PARK;
  dataSelectOptions: DataSelectOptions = {} as DataSelectOptions;
  dataParks: DataStudioParks[];
  parkSelected: DataStudioParks;
  turbinesSelected: SelectedParksTurbines[] = [];
  turbineTypes: string[] = [];
  turbineType: string;
  turbineWithCodeArray: TurbineDataStudioLive[] = [];
  assetParkTurbinesSelected: DataStudioParks[] = [];
  assetParks: DataStudioParks[] = [];
  parkNameSelected: string | null;
  turbinesSignals: TurbineDataWithSignals[];
  deviceSignalsMapper: Map<string, SignalList[]>;
  signalDevicesMapper: Map<string, { name: string, id: string }[]>;

  isPreviewActivated: boolean = false;
  pointsPerDay: number = 24 * 60 * 60 * 2; // 30s data

  devicesList: DevicesList[] = [];
  signalMultiDevicesList: SignalMultiDevicesList[] = [];
  signalsData: [DSSignalItemData[], MinMaxSignals[]];
  multiSignalsData: [DSSignalItemData[], MinMaxSignals[]];

  enumGranularity = DataStudioGranularityType;
  private enumStream = Stream;
  private enumQueryType = DataStudioQueryType;
  private unsubscribe$ = new Subject();
  turbineTypeSelection: string;
  wrongDate: boolean = false;
  aggregationOptions: { values: AggregationType[], titles: string[] } = {
    values: [AGG_TYPES.PARK, AGG_TYPES.TURBINE_TYPE, AGG_TYPES.TURBINE],
    titles: ['COMMON.PARK', 'DATA_STUDIO.DATA_PARAMETERS.TURBINE_TYPE', 'COMMON.TURBINE']
  };
  typesOptions: { values: DeviceType[], titles: string[] } = {
    values: [DEVICE_TYPES.TURBINE, DEVICE_TYPES.CONTROL_SYSTEM, DEVICE_TYPES.METMAST],
    titles: ['COMMON.TURBINE', 'DATA_STUDIO.DATA_PARAMETERS.CONTROL_SYSTEM', 'DATA_STUDIO.DATA_PARAMETERS.METMAST']
  };
  selectedAggregationName: string;
  selectedType: DeviceType;
  selectedTypeName: string;
  dataParksNames: string[];
  assetParksNames: Array<string>;
  turbineSelection: string;
  turbinesList: string[];
  controlSystemSignals: CSSignal[];
  controlSystems: DevicePark[];
  metmastsList: MetmastDevicePark[];
  metmastsSignals: Metmast[];
  queryParams: QueryParams;
  storedTurbineSignals: TurbineDataWithSignals[];
  storedCSSignals: CSSignal[];
  storedMetmastsSignals: MetmastsDataWithSignals[];
  signalTypesMapper: Map<string, Array<string>>
  sensorsList: DevicePark[]

  get turbineView() { return this.selectedType === DEVICE_TYPES.TURBINE; }
  get controlSystemView() { return this.selectedType === DEVICE_TYPES.CONTROL_SYSTEM; }
  get metmastView() { return this.selectedType === DEVICE_TYPES.METMAST; }
  get parkAggregation() { return this.selectedAggregation === AGG_TYPES.PARK; }
  get turbineAggregation() { return this.selectedAggregation === AGG_TYPES.TURBINE; }
  get turbineTypeAggregation() { return this.selectedAggregation === AGG_TYPES.TURBINE_TYPE; }

  constructor(
    private dataStudioService: DataStudioRedesignService,
    private warningService: WarningsService,
    private dataStudioHelper: DataStudioHelperService,
    private themeService: ThemeService,
    private dateTimeFormatService: DateTimeFormatService,
    private translateService: TranslateService
  ) {
    this.selectedAggregationName = this.aggregationOptions.titles[0];
    this.selectedTypeName = this.typesOptions.titles[0];
    this.selectedType = this.typesOptions.values[0]
    this.bsRangeValue = [new Date(), new Date()];
    this.initSelectOptions();
  }

  ngOnInit(): void {
    this.bsConfig = this.themeService.getTheme().pipe(
      map((theme: EThemeName) => Object.assign({}, {
        containerClass: theme,
        rangeInputFormat: this.dateTimeFormatService.getDateFormat('dmy').toLocaleUpperCase(),
        dateInputFormat: this.dateTimeFormatService.getDateFormat('dmy').toLocaleUpperCase()
      }))
    );
    this.getAllParkAndTurbines();
  }

  ngOnDestroy(): void {
    this.initSelectOptions();
    this.dataStudioHelper.setDateObservableLiveData$(this.dataSelectOptions);
    this.unsubscribe$.next(true);
    this.unsubscribe$.unsubscribe();
  }

  changeDateRange(event) {
    if (event) {
      this.wrongDate = this.dataSelectOptions.fileRequest.wrongDate =
        Math.abs(event[0].getTime() - event[1].getTime()) / 1000 / 60 / 60 / 24 > 30 ? true : false;
      const dateA = new Date(event[0].getTime());
      this.dataSelectOptions.dateFrom = Number(dateA.setHours(0, 0, 0, 0));
      this.dataSelectOptions.fileRequest.startTS = this.dataSelectOptions.dateFrom;
      const dateB = new Date(event[1].getTime());
      this.dataSelectOptions.dateTo = Number(dateB.setHours(23, 59, 59, 999));
      this.dataSelectOptions.fileRequest.endTS = this.dataSelectOptions.dateTo;
      if (this.dataSelectOptions.fileRequest.signals !== undefined) {
        this.setQueryParams(this.dataSelectOptions.dateFrom, this.dataSelectOptions.dateTo);
        this.calculateSize();
      }
      if (event[0].getTime() !== this.bsRangeValue[0].getTime() || event[1].getTime() !== this.bsRangeValue[1].getTime()) {
        this.selectType({ option: this.selectedTypeName, index: 0 });
      }
    }
  }

  selectAggregation(event) {
    this.dataSelectOptions.fileRequest.deviceIds = [];
    if (this.selectedAggregationName !== event.option) {
      this.selectedAggregationName = event.option
      this.selectedAggregation = this.aggregationOptions.values[event.index];
      this.parkSelected = null;
      this.parkNameSelected = null;
      this.turbineType = undefined;
      this.assetParkTurbinesSelected = [];
      switch (this.selectedAggregation) {
        case AGG_TYPES.PARK:
          this.dataSelectOptions.aggregationLevel = '0';
          this.selectPark();
          break;
        case AGG_TYPES.TURBINE:
          this.parkSelected = this.dataParks[0];
          this.turbineWithCodeArray = this.parkSelected.turbines.map(turbine => {
            return {
              ...turbine,
              displayName: `${this.parkSelected.fe_name} - ${turbine.code.replace(/_/g, ' ')}`,
              value: turbine.serialNav,
              parkCode: this.parkSelected.code,
              name: turbine.name,
              id: turbine._id,
              parkName: this.parkSelected.name,
              code: turbine.code
            };
          });
          this.dataSelectOptions.aggregationLevel = '1';
          this.selectedOneTurbine({ option: this.turbineWithCodeArray[0].displayName });
          this.setTurbinesList();
          break;
        case AGG_TYPES.TURBINE_TYPE:
          this.dataSelectOptions.aggregationLevel = '2';
          this.selectTurbineType({ option: this.turbineTypes[0] });
          break;
      }
    }
  }

  selectType({ option, index }) {
    if (this.selectedTypeName !== option) {
      this.selectedTypeName = option;
      this.selectedType = this.typesOptions.values[index];
    }
    this.dataStudioHelper.typeSelection = this.selectedType;
    if (this.controlSystemView) {
      this.getCSSignalsByParkName();
    } else if (this.metmastView) {
      this.getMetMastsignalsByParkName();
    } else if (this.turbineView) {
      this.getSignalsByParkName();
    }
  }

  // signals selection for turbine updated
  getSelectedTurbineSignals(signals: TurbineSignal[]) {
    this.dataSelectOptions.fileRequest.signals = signals.map(signal => signal.value);
    this.calculateSize();
    if (signals.length) {
      const turbineIds = this.turbinesSelected.flatMap(park => park.turbines.map(turbine => turbine._id));
      this.mapSignalsToDevices();
      this.devicesList.forEach(device => {
        device.signals = device.signals.filter(deviceSignal =>
          signals.map(signal => signal.value).includes(deviceSignal.name))
      });
      this.getTurbineSignalsData([turbineIds[0]], [signals[0].value]);
      this.signalMultiDevicesList = signals.map((signal, index) => ({
        name: signal.value,
        displayName: this.translateService.instant(signal.name),
        selected: !index,
        devices: this.signalDevicesMapper
          .get(signal.value)?.map(device => ({ ...device, selected: false }))
          .filter(device => this.dataSelectOptions.fileRequest.turbines.includes(device.id))
      }));
    }
  }

  // signals selection for control system updated
  getSelectedCSSignals(signals: CSSignal[]) {
    this.dataSelectOptions.fileRequest.signals = signals.map(signal => signal.dbName);
    this.calculateSize();
    if (signals.length) {
      this.mapSignalsToDevices();
      this.devicesList.forEach(device => {
        device.signals = device.signals.filter(deviceSignal =>
          signals.map(signal => signal.dbName).includes(deviceSignal.name))
      });
      this.getControlSystemSignalData(signals[0].deviceId, [signals[0].dbName]);
      this.signalMultiDevicesList = signals.map((signal, index) => ({
        name: signal.dbName,
        displayName: signal.displayName,
        selected: !index,
        devices: this.signalDevicesMapper
          .get(signal.dbName)?.map(device => ({ ...device, selected: false }))
          .filter(device => this.dataSelectOptions.fileRequest.deviceIds.includes(device.id))
      }));
    }
  }

  // signals selection for metmast updated
  getSelectedMetmastSignals(signals: MetmastSignal[]) {
    this.dataSelectOptions.fileRequest.signals = [...new Set(signals.map(signal => signal.dbName))];
    if (signals.length) {
      const deviceIds = signals.map(signal => signal.deviceId);
      this.dataSelectOptions.fileRequest.deviceIds = deviceIds;
      this.calculateSize();
      this.devicesList = this.setMetmastDeviceList().filter(device => deviceIds.includes(device.id))
        .map((device, index) => ({
          ...device, selected: !index, signals: device.signals.filter(deviceSignal =>
            signals.map(signal => signal.dbName).includes(deviceSignal.name))
        }));
      this.mapSignalsToDevices();
      this.getMetmastSignalData([signals[0].deviceId], [signals[0].dbName]);
      this.signalMultiDevicesList = [...new Map(signals.map(signal => [signal.dbName, signal])).values()].map((signal, index) => ({
        name: signal.dbName,
        displayName: signal.displayName,
        selected: !index,
        devices: this.signalDevicesMapper
          .get(signal.dbName)?.filter(device => deviceIds.includes(device.id))
          .map(device => ({ ...device, selected: false }))
          .filter(device => this.dataSelectOptions.fileRequest.deviceIds.includes(device.id))
      }));
    } else {
      this.dataSelectOptions.fileRequest.deviceIds = [];
    }
  }

  selectTurbineType({ option }) {
    this.turbineTypeSelection = option;
    this.turbinesSelected = [];
    this.getSignalsByTurbineType(option);
    this.turbineType = option;
    this.filterAssetsByTurbineType();
    this.dataSelectOptions.estimatedRows = 0;
    this.dataSelectOptions.estimatedFields = 0;
    this.dataSelectOptions.estimatedFileSize = 0;
    this.calculateSize();
  }

  selectPark(event = null) {
    this.parkSelected = event ? this.dataParks[event.index] : this.dataParks[0];
    this.resetTypeSelection();
    if (this.turbineAggregation) {
      this.turbineWithCodeArray = this.parkSelected.turbines.map(e => {
        return {
          ...e,
          displayName: `${this.parkSelected.fe_name} - ${e.code.replace(/_/g, ' ')}`,
          value: e.serialNav, parkCode: this.parkSelected.code, name: e.name, id: e._id, parkName: this.parkSelected.name, code: e.code
        };
      });
      this.selectedOneTurbine({ option: this.turbineWithCodeArray[0].displayName });
      this.setTurbinesList();
    } else {
      this.getAvailableTSForPark();
      this.getSignalsByParkName();
      this.parkSelected.turbines.sort(this.dataStudioHelper.naturalCompare);
      this.controlSystems = this.parkSelected.controlSystem;
      this.metmastsList = this.parkSelected.metmasts;
      this.dataSelectOptions.assetPark = this.parkSelected.code;
    }
  }

  selectedOneTurbine({ option }) {
    const turbine = this.turbineWithCodeArray.find(item => item.displayName === option);
    if (turbine) {
      this.turbinesSelected = [{ parkCode: this.parkSelected.code, turbines: [turbine] }];
      this.turbineSelection = turbine.displayName;
      this.getAvailableTSForPark(turbine.parkName);
      this.getSignalsByTurbineName(turbine.name);
      this.dataSelectOptions.assetPark = turbine.parkCode;
      this.dataSelectOptions.assetSelected = turbine.value;
      this.dataSelectOptions.fileRequest.turbines.push(turbine.id);
      this.setTurbineDeviceList();
      this.calculateSize();
    }
  }

  // control system selection updated
  selectedAssetControlSystem(controlSystem: DevicePark[]) {
    this.dataSelectOptions.fileRequest.deviceIds = [...controlSystem.map(device => device._id)];
    this.dataSelectOptions.fileRequest.signals = [];
    const deviceIds = controlSystem.map(device => device._id);
    this.controlSystemSignals = this.storedCSSignals?.filter(signal => deviceIds.length && deviceIds.filter(id => signal.deviceId.includes(id)));
    this.devicesList = this.controlSystems.filter(cs => deviceIds.includes(cs._id)).map((device, index) => ({
      name: device.name,
      id: device._id,
      selected: !index,
      signals: this.deviceSignalsMapper?.get(device._id) || []
    }));
    this.calculateSize();
  }

  // metmast selection updated
  selectedAssetMetmasts(metmasts: MetmastDevicePark[]) {
    this.dataSelectOptions.fileRequest.deviceIds = [];
    this.sensorsList = [...metmasts.flatMap(device => device.sensorsList)];
    this.dataSelectOptions.fileRequest.signals = [];
    this.metmastsSignals = this.storedMetmastsSignals?.filter(metmast => metmasts.map(device => device._id).includes(metmast._id));
    this.devicesList = this.setMetmastDeviceList();
    this.calculateSize();
  }

  // turbine selection updated
  selectedAssetTurbines(park: SelectedParksTurbines) {
    if (!this.turbinesSelected.find(data => data.parkCode === park.parkCode)) {
      this.turbinesSelected.push(park);
    } else {
      this.turbinesSelected[(this.turbinesSelected.findIndex(park => park.parkCode === park.parkCode))].turbines = park.turbines;
    }
    const onlyTurbines = [];
    this.turbinesSelected.forEach(element => {
      element.turbines.forEach(turbine => {
        onlyTurbines.push(turbine._id);
      });
    });
    this.dataSelectOptions.fileRequest.turbines = onlyTurbines;
    this.turbinesSignals = this.storedTurbineSignals?.filter(turbine => onlyTurbines.includes(turbine._id));
    this.setTurbineDeviceList();
    this.calculateSize();
  }

  addPark() {
    const selectedPark = this.assetParks.find(park => park.fe_name === this.parkNameSelected);
    this.getAvailableTSForPark(selectedPark.name);
    this.assetParkTurbinesSelected.push(selectedPark);
    this.assetParkTurbinesSelected.forEach(element => {
      element.turbines.sort(this.dataStudioHelper.naturalCompare);
    });
    this.setTurbineDeviceList();
    this.assetParks = this.assetParks
      .filter((park) => park.fe_name !== this.parkNameSelected)
      .sort();
    this.setAssetNames();
    this.parkNameSelected = null;
  }

  removeParkList(event) {
    this.assetParkTurbinesSelected = this.assetParkTurbinesSelected.filter(
      (park) => park.name !== event.parkName
    );
    this.assetParks.push(this.dataParks.find(park => park.name === event.parkName));
    this.assetParks.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
    event.turbines.forEach(turbine => {
      for (let i = 0; i < this.dataSelectOptions.fileRequest.turbines.length; i++) {
        if (turbine._id === this.dataSelectOptions.fileRequest.turbines[i]) {
          this.dataSelectOptions.fileRequest.turbines.splice(i, 1);
        }
      }
    });
    this.calculateSize();
  }

  selectParkAsset(event) {
    this.parkNameSelected = event.option;
  }

  closePreview() {
    this.showPreview = false;
  }

  getPreviewData({ devicesIds, signalsIds, multiDevices }) {
    this.turbineView ? this.getTurbineSignalsData(devicesIds, signalsIds, multiDevices) :
      this.controlSystemView ? this.getControlSystemSignalData(devicesIds, signalsIds, multiDevices) :
        this.getMetmastSignalData(devicesIds, signalsIds, multiDevices);
  }

  private setMetmastDeviceList() {
    const sensorIds = this.sensorsList.map(sensor => sensor._id);
    return this.metmastsList.flatMap(mm => mm.sensorsList.filter(sensor => sensorIds.includes(sensor._id)))
      .map((sensor, index) => ({
        name: sensor.name,
        id: sensor._id,
        selected: !index,
        signals: this.deviceSignalsMapper?.get(sensor._id) || []
      }))
  }

  private setTurbineDeviceList() {
    this.devicesList = this.turbinesSelected.flatMap(park => park.turbines.map((turbine, index) => ({
      name: turbine.code.replace(/_/g, ' '),
      id: turbine._id,
      selected: !index,
      signals: this.deviceSignalsMapper?.get(turbine._id) || []
    })));
  }

  private filterAssetsByTurbineType() {
    this.assetParkTurbinesSelected = [];
    const filteredArray: Array<DataStudioParks> = [];
    this.dataParks.forEach(park => {
      park.turbines.forEach(turbine => {
        if (turbine.type === this.turbineType && !filteredArray.includes(park)) {
          filteredArray.push(park);
        }
      });
    });
    this.assetParks = filteredArray;
    this.setAssetNames();
  }


  // get Turbine signals data for preview
  private getTurbineSignalsData(ids, signalIds, multiDevices = false) {
    this.dataStudioService.requestTurbineSignalsData(ids, signalIds, this.queryParams)
      .pipe(filter(response => !!response))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response: any) => {
        this.dataStudioService.requestingSignalsData = false;
        multiDevices ? this.multiSignalsData = response : this.signalsData = response;
      }
      );
  }

  // get Control system signals data for preview
  private getControlSystemSignalData(ids, signalIds, multiDevices = false) {
    this.dataStudioService.requestControlSystemData(ids, signalIds, this.queryParams)
      .pipe(filter(response => !!response))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        this.dataStudioService.requestingSignalsData = false;
        multiDevices ? this.multiSignalsData = data : this.signalsData = data;
      })
  }

  // get Metmast signals data for preview
  private getMetmastSignalData(ids, signalIds, multiDevices = false) {
    this.dataStudioService.requestMetmast(ids, signalIds, this.queryParams)
      .pipe(filter(response => !!response))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        this.dataStudioService.requestingSignalsData = false;
        multiDevices ? this.multiSignalsData = data : this.signalsData = data;
      })
  }

  private setQueryParams(startTS, endTS) {
    this.queryParams = {
      queryType: this.enumQueryType.Live,
      granularity: this.enumGranularity.G30S,
      startTS,
      endTS
    };
  }

  private getSignalsByTurbineType(turbineType: string) {
    this.dataStudioService.getAllSignalsByTurbineType(turbineType, this.enumStream.S30s)
      .pipe(filter(response => !!response))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(turbineSignals => {
        this.setTurbineSignals(turbineSignals);
      });
  }

  private getSignalsByParkName() {
    this.dataStudioService.getAllSignalsByParkName(this.parkSelected.name, this.enumStream.S30s)
      .pipe(filter(response => !!response?.[0]?.turbines))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((parkSignals: [{ turbines: TurbineDataWithSignals[] }]) => {
        this.setTurbineSignals(parkSignals[0].turbines);
      });
  }

  private getCSSignalsByParkName() {
    this.dataStudioService.getCSSignalsByParkName(this.parkSelected.name)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((controlSystem: CSDataWithSignals[]) => {
        const controlSystemList = this.getControlSystemList(controlSystem);
        this.storedCSSignals = structuredClone(controlSystemList);
        this.controlSystemSignals = controlSystemList;
        this.deviceSignalsMapper = createCSDeviceSignalsMapper(controlSystem);
        this.mapSignalsToDevices();
        this.signalDevicesMapper = createCSSignalDevicesMapper(controlSystem);
      });
  }

  private getMetMastsignalsByParkName() {
    this.dataStudioService.getMetMastsignalsByParkName(this.parkSelected.name)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(metmasts => {
        this.storedMetmastsSignals = structuredClone(metmasts);
        this.metmastsSignals = metmasts;
        this.deviceSignalsMapper = createMetmastsDeviceSignalsMapper(metmasts);
        this.mapSignalsToDevices();
        this.signalDevicesMapper = createMetmastsSignalDevicesMapper(metmasts);
      });
  }

  private getSignalsByTurbineName(turbineName: string) {
    this.dataStudioService.getAllSignalsByTurbineName(turbineName, this.enumStream.S30s)
      .pipe(filter(response => !!response))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(turbineSignals => {
        this.setTurbineSignals(turbineSignals);
      });
  }

  private setTurbineSignals(turbineSignals) {
    this.storedTurbineSignals = structuredClone(turbineSignals);
    this.turbinesSignals = turbineSignals;
    this.deviceSignalsMapper = createTurbineDeviceSignalsMapper(turbineSignals);
    this.signalTypesMapper = createTurbineSignalTypesMaper(turbineSignals);
    this.signalDevicesMapper = createTurbineSignalDevicesMapper(turbineSignals);
    this.mapSignalsToDevices();
  }

  private mapSignalsToDevices() {
    this.devicesList.forEach(device => { device.signals = this.deviceSignalsMapper?.get(device.id) || [] });
  }

  private getAllParkAndTurbines() {
    this.dataStudioService.getAllParkAndTurbines()
      .pipe(filter(response => !!response))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(response => {
        this.dataParks = structuredClone(response).filter(item => item.data30sAvailable)
          .sort((a, b) => {
            return a.fe_name.localeCompare(b.fe_name);
          });
        this.assetParks = structuredClone(this.dataParks);
        this.dataParksNames = this.dataParks.map(park => park.fe_name);
        this.setAssetNames();
        this.getTurbineTypes(this.dataParks);
        this.getTurbineWithCode(this.dataParks);
        this.initSelectOptions();
        this.selectPark();
      });
  }

  private getTurbineTypes(parks: DataStudioParks[]) {
    this.turbineTypes = Array.from(
      new Set(parks.flatMap(element => element.turbines?.map(turbine => turbine?.type) ?? []))
    ).sort();
  }

  private getTurbineWithCode(parks: DataStudioParks[]) {
    const newTurbines: TurbineDataStudioLive[] = parks.flatMap(park => {
      const sortedTurbines = [...park.turbines].sort(this.dataStudioHelper.naturalCompare);

      return sortedTurbines.map(turbine => ({
        ...turbine,
        displayName: `${park.name} - ${turbine.code.replace(/_/g, ' ')}`,
        value: turbine.serialNav,
        parkCode: park.code,
        name: turbine.name,
        id: turbine._id,
        parkName: park.name,
        code: turbine.code
      }));
    });

    this.turbineWithCodeArray = [...this.turbineWithCodeArray, ...newTurbines];
    this.setTurbinesList();
  }

  private getControlSystemList(controlSystem: CSDataWithSignals[]): CSSignal[] {
    const map = new Map<string, CSSignal>();

    controlSystem.forEach(csItem => {
      csItem.signalsList.forEach(item => {
        const existing = map.get(item.dbName);

        if (existing) {
          existing.deviceId.push(csItem._id);
        } else {
          map.set(item.dbName, {
            dbName: item.dbName || '',
            displayName: item.displayName || '',
            deviceId: [csItem._id],
            unit: item.unit || ''
          });
        }
      });
    });

    return Array.from(map.values());
  }

  private calculateSize() {
    const days = this.warningService.getDays(this.dataSelectOptions.dateFrom, this.dataSelectOptions.dateTo);
    const columns = 5 + (this.dataSelectOptions.fileRequest.signals.length);  // Right now 5 fields: Date UTC, Turbine Timezone date, User Timezone date, Turbine Code, Turbine Serial
    let rows = this.pointsPerDay * days;
    let fields = columns * rows;
    if (this.dataSelectOptions.aggregationLevel === '0' || this.dataSelectOptions.aggregationLevel === '2') { // ParkLevel  -> we multiply rows & fields for the number of turbines before getting the file size
      let items = 0;
      const elements = this.dataStudioHelper.typeSelection === DEVICE_TYPES.TURBINE ? 'turbines' : 'deviceIds';
      this.dataSelectOptions.fileRequest[elements].forEach(_ => {
        items++;
      });
      rows = rows * items;
      fields = fields * items;
      this.dataSelectOptions.estimatedRows = rows;
      this.dataSelectOptions.estimatedFields = fields;
      this.dataSelectOptions.estimatedFileSize = this.warningService.getFileSize(fields);
    } else {
      this.dataSelectOptions.estimatedRows = rows;
      this.dataSelectOptions.estimatedFields = fields;
      this.dataSelectOptions.estimatedFileSize = this.warningService.getFileSize(fields);
    }
    this.dataStudioHelper.setDateObservableLiveData$(this.dataSelectOptions);
  }

  private initSelectOptions() {
    this.dataSelectOptions.aggregationLevels = [AGG_TYPES.PARK, AGG_TYPES.TURBINE, AGG_TYPES.TURBINE_TYPE];
    this.dataSelectOptions.aggregationLevel = '0';
    this.dataSelectOptions.assetSelected = null;
    this.dataSelectOptions.assetPark = null;
    this.dataSelectOptions.dateFrom = new Date().setHours(0, 0, 0, 0);
    this.dataSelectOptions.dateTo = new Date().getTime();
    this.dataSelectOptions.estimatedRows = 0;
    this.dataSelectOptions.estimatedFields = 0;
    this.dataSelectOptions.estimatedFileSize = 0;
    this.dataSelectOptions.fileRequest = {
      wrongDate: false,
      fileName: '',
      turbines: [],
      deviceIds: [],
      signals: [],
      granularity: '30s',
      queryType: this.enumQueryType.Live,
      deviceType: DEVICE_TYPES.TURBINE,
      fileType: '',
      startTS: Number(this.dataSelectOptions.dateFrom),
      endTS: Number(this.dataSelectOptions.dateTo)
    };
    this.setQueryParams(this.dataSelectOptions.dateFrom, this.dataSelectOptions.dateTo);
  }

  private getAvailableTSForPark(parkName = this.parkSelected.name) {
    this.dataStudioService.getAvailableTS(parkName).pipe(takeUntil(this.unsubscribe$)).subscribe(data => {
      if (data) {
        this.minDate = new Date(data);
      }
    });
  }

  private setAssetNames() {
    this.assetParksNames = this.assetParks.map(asset => asset.fe_name);
  }

  private setTurbinesList() {
    this.turbinesList = this.turbineWithCodeArray.map(turbine => turbine.displayName);
  }

  private resetTypeSelection() {
    this.turbinesSelected = [];
    this.dataStudioHelper.typeSelection = DEVICE_TYPES.TURBINE;
    this.dataSelectOptions.fileRequest.deviceIds = [];
    this.selectedTypeName = this.typesOptions.titles[0];
    this.selectedType = this.typesOptions.values[0];
  }
}
